/**
 * Client-side confirmation policy matching + date preview.
 * Mirrors backend confirmationPolicyMatch (score + DOJ-based probation dates).
 */

function addDays(dateInput, days) {
  const d = new Date(dateInput);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + Number(days || 0));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function computeProbationDays(policy) {
  const payload = policy || {};
  if (payload.customProbationDays && Number(payload.customProbationDays) > 0) {
    return Number(payload.customProbationDays);
  }
  if (payload.probationDays && Number(payload.probationDays) > 0) {
    return Number(payload.probationDays);
  }
  const period = String(payload.initialProbationPeriod || "3_months");
  if (period === "6_months") return 180;
  if (period === "1_month") return 30;
  if (period === "2_months") return 60;
  return 90;
}

/** Confirmation / probation dates always from date of join. */
export function resolveProbationDatesFromJoin(policy, dateOfJoin) {
  const start = dateOfJoin
    ? String(dateOfJoin).slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  const days = computeProbationDays(policy);
  const end = addDays(start, days);
  return {
    probationStartDate: start,
    probationEndDate: end,
    expectedConfirmationDate: end,
    probationDays: days,
  };
}

function scorePolicyMatch(policy, employeeContext) {
  const assignedTo = policy.assignedTo || {};
  if (assignedTo.allEmployees || policy.allEmployees) {
    return { matches: true, score: 1 };
  }

  let score = 0;
  let anyDimensionConfigured = false;
  const checks = [
    ["branches", employeeContext.branchId],
    ["departments", employeeContext.departmentId],
    ["designations", employeeContext.designationId],
    ["grades", employeeContext.gradeId],
    ["categories", employeeContext.categoryId],
    ["subDepartments", employeeContext.subDepartmentId],
  ];

  for (const [key, value] of checks) {
    const ids = assignedTo[key] || [];
    if (ids.length > 0) {
      anyDimensionConfigured = true;
      const num = Number(value);
      if (value != null && value !== "" && ids.some((id) => Number(id) === num)) {
        score += 10;
      }
    }
  }

  const empTypes = assignedTo.employmentTypes || [];
  if (empTypes.length > 0) {
    anyDimensionConfigured = true;
    const et = employeeContext.employmentType;
    if (
      et &&
      empTypes.some(
        (t) =>
          String(t).toLowerCase().replace(/[-\s]/g, "_") ===
          String(et).toLowerCase().replace(/[-\s]/g, "_")
      )
    ) {
      score += 8;
    }
  }

  const employees = assignedTo.employees || [];
  if (employees.length > 0) {
    anyDimensionConfigured = true;
    if (
      employeeContext.employeeId != null &&
      employees.some((id) => Number(id) === Number(employeeContext.employeeId))
    ) {
      score += 50;
    }
  }

  if (!anyDimensionConfigured) return { matches: false, score: 0 };
  return { matches: score > 0, score };
}

/**
 * Find best matching active confirmation policy.
 * @param {object} employeeContext - org fields from create/edit form
 * @param {array} policies
 * @param {Date|string|null} referenceDate - defaults to today (policy must be effective by this date)
 */
export function findApplicableConfirmationPolicy(
  employeeContext,
  policies = [],
  referenceDate = null
) {
  if (!employeeContext || !policies?.length) return null;

  const refDate = referenceDate ? new Date(referenceDate) : new Date();
  refDate.setHours(0, 0, 0, 0);

  const doj = employeeContext.dateOfJoin || employeeContext.joiningDate;
  if (doj) {
    const joinDate = new Date(doj);
    joinDate.setHours(0, 0, 0, 0);
    if (joinDate > refDate) return null;
  }

  const eligible = policies.filter((p) => {
    if (p.status !== "active") return false;
    if (p.effectiveDate) {
      const eff = new Date(p.effectiveDate);
      eff.setHours(0, 0, 0, 0);
      if (eff > refDate) return false;
    }
    return true;
  });

  const scored = eligible
    .map((p) => {
      const { matches, score } = scorePolicyMatch(p, employeeContext);
      return { policy: p, matches, score };
    })
    .filter((x) => x.matches);

  if (!scored.length) return null;

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const dateA = new Date(a.policy.effectiveDate || 0).getTime();
    const dateB = new Date(b.policy.effectiveDate || 0).getTime();
    return dateB - dateA;
  });

  return scored[0].policy;
}

/**
 * Preview policy confirmation date for Probation status on the employee form.
 */
export function previewPolicyConfirmationDate(formData, policies = []) {
  const status = String(formData?.employmentStatus || "").trim();
  if (status !== "Probation") {
    return { date: null, policy: null, probationDays: null };
  }

  const context = {
    employeeId: formData.id || null,
    branchId: formData.branchId,
    departmentId: formData.departmentId,
    designationId: formData.designationId,
    gradeId: formData.gradeId,
    categoryId: formData.categoryId,
    subDepartmentId: formData.subDepartmentId,
    employmentType: formData.employmentType,
    dateOfJoin: formData.joiningDate || formData.dateOfJoin,
    joiningDate: formData.joiningDate || formData.dateOfJoin,
  };

  if (!context.joiningDate) {
    return { date: null, policy: null, probationDays: null };
  }

  const policy = findApplicableConfirmationPolicy(context, policies);
  if (!policy) {
    return { date: null, policy: null, probationDays: null };
  }

  const dates = resolveProbationDatesFromJoin(policy, context.joiningDate);
  return {
    date: dates.expectedConfirmationDate,
    policy,
    probationDays: dates.probationDays,
    probationStartDate: dates.probationStartDate,
  };
}

export function formatIsoDateDisplay(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
}
